import { Fragment, useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { Icon } from 'components/ui/Icon'
import { AreaOption } from 'utils/areas'

type AreaComboboxProps = {
  options: AreaOption[]
  selectedAreaId?: string
  onSelect: (areaId: string) => void
  triggerPlaceholder: string
  searchPlaceholder: string
  noResultsLabel: string
}

// ChatGPT-model-selector-style area picker for hierarchical areas: a button trigger shows the
// current "Parent › Child" selection; opening reveals a search field + the flat list of selectable
// areas (each with its path). Scales to large/deep trees. Only used when areas actually nest —
// flat area sets keep the simpler pill picker.
export const AreaCombobox = ({
  options,
  selectedAreaId,
  onSelect,
  triggerPlaceholder,
  searchPlaceholder,
  noResultsLabel,
}: AreaComboboxProps) => {
  const [query, setQuery] = useState('')

  const selected = options.find((option) => option.area._id === selectedAreaId) ?? null
  const normalized = query.trim().toLowerCase()
  const filtered = normalized
    ? options.filter((option) => option.path.join(' ').toLowerCase().includes(normalized))
    : options

  return (
    <Combobox value={selected} onChange={(option: AreaOption | null) => option && onSelect(option.area._id)}>
      {({ open }) => (
        <div className="tw-relative tw-w-full">
          <Combobox.Button
            className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-transition"
            style={{ border: '1px solid var(--page-border)', background: 'var(--input-bg)' }}
          >
            <span
              className="tw-min-w-0 tw-flex-1 tw-truncate"
              style={{ color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
            >
              {selected ? selected.path.join(' › ') : triggerPlaceholder}
            </span>
            <Icon
              icon={['far', 'chevron-down']}
              className={clsx('tw-h-3 tw-w-3 tw-shrink-0 tw-transition-transform', open && 'tw-rotate-180')}
              style={{ color: 'var(--text-tertiary)' }}
            />
          </Combobox.Button>

          <Transition
            as={Fragment}
            enter="tw-transition tw-ease-out tw-duration-150"
            enterFrom="tw-opacity-0 tw--translate-y-1"
            enterTo="tw-opacity-100 tw-translate-y-0"
            leave="tw-transition tw-ease-in tw-duration-100"
            leaveFrom="tw-opacity-100"
            leaveTo="tw-opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options
              className="tw-absolute tw-z-20 tw-mt-1 tw-w-full tw-overflow-hidden tw-rounded-2xl tw-shadow-lg"
              style={{ background: 'var(--surface-solid)', border: '1px solid var(--border-default)' }}
            >
              <div
                className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2"
                style={{ borderBottom: '1px solid var(--border-default)' }}
              >
                <Icon
                  icon={['far', 'magnifying-glass']}
                  className="tw-h-4 tw-w-4 tw-shrink-0"
                  style={{ color: 'var(--text-tertiary)' }}
                />
                <Combobox.Input
                  autoFocus
                  className="tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-[var(--text-tertiary)]"
                  style={{ color: 'var(--text-primary)' }}
                  placeholder={searchPlaceholder}
                  displayValue={() => ''}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>

              <div className="scrollbar-hidden tw-max-h-60 tw-overflow-auto tw-py-1">
                {filtered.length === 0 ? (
                  <div className="tw-px-4 tw-py-2 tw-text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    {noResultsLabel}
                  </div>
                ) : (
                  filtered.map((option) => (
                    <Combobox.Option
                      key={option.area._id}
                      value={option}
                      className={({ active }) =>
                        clsx(
                          'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm',
                          active && 'tw-bg-[var(--page-surface-hover)]',
                        )
                      }
                    >
                      {({ selected: isSelected }) => (
                        <>
                          <span className="tw-min-w-0 tw-flex-1 tw-truncate" style={{ color: 'var(--text-secondary)' }}>
                            {option.path.length > 1 && `${option.path.slice(0, -1).join(' › ')} › `}
                            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {option.path[option.path.length - 1]}
                            </span>
                          </span>
                          {isSelected && (
                            <Icon
                              icon={['far', 'check']}
                              className="tw-h-3 tw-w-3 tw-shrink-0"
                              style={{ color: 'var(--primary-500)' }}
                            />
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </div>
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  )
}
